<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'

import BrandButton from '@/components/ui/BrandButton.vue'

const links = [
    { label: 'Služby', href: '/#sluzby' },
    { label: 'O mně', href: '/#about' },
    { label: 'Reference', href: '/#reference' },
    { label: 'Blogy', href: '/blog' },
    { label: 'Kontakt', href: '/#contact' },
]

const mobileOpen = ref(false)
const scrolled = ref(false)

const onScroll = () => {
    scrolled.value = window.scrollY > 8
}

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
})

const handleNavigate = () => {
    mobileOpen.value = false
}
</script>

<template>
    <header
        class="fixed top-0 left-0 z-50 w-full transition-colors duration-300"
        :class="scrolled || mobileOpen ? 'bg-white/95 shadow-sm backdrop-blur' : 'bg-white/80 backdrop-blur'"
    >
        <div class="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
            <a
                class="text-brand-900 flex items-center gap-3"
                href="/"
                aria-label="SolveCount - domovská stránka"
            >
                <span
                    class="bg-brand-900 grid size-9 grid-cols-2 grid-rows-2 gap-px overflow-hidden rounded-md p-1"
                    aria-hidden="true"
                >
                    <span class="bg-white" />
                    <span class="bg-transparent" />
                    <span class="bg-transparent" />
                    <span class="bg-white" />
                </span>
                <span class="text-xl font-bold tracking-tight">SolveCount</span>
            </a>

            <nav
                class="hidden items-center gap-10 md:flex"
                aria-label="Hlavní navigace"
            >
                <a
                    class="text-brand-900 hover:text-brand-700 text-sm font-semibold tracking-wide transition-colors"
                    v-for="link in links"
                    :key="link.href"
                    :href="link.href"
                >
                    {{ link.label }}
                </a>

                <Transition name="fade">
                    <BrandButton
                        v-if="scrolled"
                        size="sm"
                        href="/#contact"
                    >
                        Konzultace
                    </BrandButton>
                </Transition>
            </nav>

            <button
                class="text-brand-900 rounded-md p-2 md:hidden"
                type="button"
                aria-label="Otevřít menu"
                :aria-expanded="mobileOpen"
                @click="mobileOpen = !mobileOpen"
            >
                <Bars3Icon
                    class="size-6"
                    v-if="!mobileOpen"
                />

                <XMarkIcon
                    class="size-6"
                    v-else
                />
            </button>
        </div>

        <Transition name="slide">
            <div
                class="border-t border-zinc-100 bg-white md:hidden"
                v-if="mobileOpen"
            >
                <nav
                    class="flex flex-col gap-1 px-6 py-4"
                    aria-label="Mobilní navigace"
                >
                    <a
                        class="text-brand-900 rounded-md px-3 py-3 text-base font-semibold hover:bg-zinc-50"
                        v-for="link in links"
                        :key="link.href"
                        :href="link.href"
                        @click="handleNavigate"
                    >
                        {{ link.label }}
                    </a>

                    <BrandButton
                        class="mt-3"
                        href="/#contact"
                        block
                        @click="handleNavigate"
                    >
                        Konzultace
                    </BrandButton>
                </nav>
            </div>
        </Transition>
    </header>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
